import { useEffect, useRef } from 'react'

import { usePrevious } from '@sequencehq/utils'

import { useLogEvent } from 'lib/postHog/useLogEvent'
import type { AutomationAnalysis, SimulationResult } from './simulation.types'

const SIMULATION_EVENT_NAME = 'automation_simulation_completed'

/**
 * Sends one event per finished simulation, carrying how long the whole thing
 * took, how long each step took, and what it decided.
 *
 * One event rather than one per step, because the interesting questions are
 * about the run as a whole: is this fast enough to do while somebody edits a
 * quote, and which step is the reason when it isn't.
 *
 *   read the workflow -> fetch what it reads -> run the conditions
 *      analysisMs          dependenciesMs        evaluationMs
 *   |______________________________________________________|
 *                          totalMs
 */
export const useSimulationTelemetry = ({
  analysis,
  analysisDurationMs,
  evaluationDurationMs,
  isEnabled,
  isPending,
  isResolvingDependencies,
  result
}: {
  analysis: AutomationAnalysis | undefined
  analysisDurationMs: number | undefined
  evaluationDurationMs: number | undefined
  isEnabled: boolean
  isPending: boolean
  isResolvingDependencies: boolean
  result: SimulationResult | undefined
}) => {
  const { logEvent } = useLogEvent()

  const runStartedAt = useRef<number | null>(null)
  const dependenciesStartedAt = useRef<number | null>(null)
  const dependenciesDurationMs = useRef<number | undefined>(undefined)
  // The answer we've already sent, so a re-render for some other reason doesn't
  // send it a second time.
  const loggedResult = useRef<SimulationResult | undefined>(undefined)

  const wasPending = usePrevious(isPending)
  const wasResolvingDependencies = usePrevious(isResolvingDependencies)

  // The fetching has no duration of its own to report, so we take it from the
  // hook going busy and then idle again. `usePrevious` is undefined on the
  // first render, which reads as "wasn't busy" and starts the clock correctly.
  useEffect(() => {
    if (isResolvingDependencies && !wasResolvingDependencies) {
      dependenciesStartedAt.current = performance.now()
      return
    }

    const startedAt = dependenciesStartedAt.current

    if (!isResolvingDependencies && wasResolvingDependencies && startedAt) {
      dependenciesDurationMs.current = Math.round(performance.now() - startedAt)
      dependenciesStartedAt.current = null
    }
  }, [isResolvingDependencies, wasResolvingDependencies])

  useEffect(() => {
    if (isEnabled && isPending && !wasPending) {
      runStartedAt.current = performance.now()
    }
  }, [isEnabled, isPending, wasPending])

  // biome-ignore lint/correctness/useExhaustiveDependencies: `logEvent` is rebuilt on every render, so listing it would send the event again and again for one answer
  useEffect(() => {
    if (!isEnabled || isPending || !result) return
    if (loggedResult.current === result) return

    loggedResult.current = result
    const startedAt = runStartedAt.current
    runStartedAt.current = null

    logEvent({
      eventName: SIMULATION_EVENT_NAME,
      metadata: {
        // Zero when the answer needed no work at all, which happens when
        // everything was already cached and the run never went busy.
        totalMs: startedAt ? Math.round(performance.now() - startedAt) : 0,
        analysisMs: analysisDurationMs,
        dependenciesMs: dependenciesDurationMs.current,
        evaluationMs: evaluationDurationMs,
        dependencyCount: analysis?.dependencies.length,
        pathCount: analysis?.paths.length,
        outcome: result.outcome,
        ...(result.outcome === 'RESOLVED'
          ? { terminalType: result.terminalType }
          : { reason: result.reason })
      }
    })
  }, [
    analysis,
    analysisDurationMs,
    evaluationDurationMs,
    isEnabled,
    isPending,
    result
  ])
}
